import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { ChatSection } from '@/components/inbox/ChatSection';
import { DesktopInquiriesListView } from '@/components/inbox/DesktopInquiriesListView';
import { InboxListHeader } from '@/components/inbox/InboxListHeader';
import { InboxSidebarPanel } from '@/components/inbox/InboxSidebarPanel';
import { MobileDraftsTab } from '@/components/inbox/MobileDraftsTab';
import { NewSupportMessageFab } from '@/components/inbox/NewSupportMessageFab';
import { TAB_ROUTES } from '@/components/inbox/constants';
import { inboxSidebarOpenAtom } from '@/components/inbox/inboxSidebarState';
import { LIST_DEFAULT_WIDTH, nextListWidth } from '@/components/inbox/listColumnWidth';
import { type DesktopInboxProps } from '@/components/inbox/types';
import { Box } from '@/components/ui/box';
import { Pressable } from '@/components/ui/pressable';
import { VStack } from '@/components/ui/vstack';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { useInboxGrouping } from '@/hooks/inbox/useInboxGrouping';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useRefreshOnFocus } from '@/hooks/useRefreshOnFocus';
import {
  getClosedInquiriesV2QueryOptions,
  getAllInquiriesV2QueryOptions,
  getOpenInquiriesV2QueryOptions,
  type InquiryV2,
} from '@/services/getInquiriesV2';
import { getInquiryStatsQueryOptions } from '@/services/getInquiryStats';
import { getInquiryV2QueryOptions, type GetInquiryV2Response } from '@/services/getInquiryV2';
import { shouldFetchUserScoped } from '@/util/authTokens';
import {
  dedupeInquiriesByConversationId,
  filterClosedTabInquiriesForUser,
  filterOpenTabInquiriesForUser,
} from '@/util/inquiryGroups';
import { Sentry } from '@/util/sentry';

export const DesktopInbox = memo(function DesktopInbox({
  userId,
  selectedChatId,
  selectedCancerFocus,
  selectedCancerFocusId,
  inquiryCancerTypesQuery,
  tabSelected,
  tabState,
  unreadByTab,
  onIndexChange,
  setTabSelected,
  setSelectedCancerFocus,
  resetSelectedChatId,
  resetSelectedCancerFocus,
}: DesktopInboxProps) {
  const queryClient = useQueryClient();

  const inboxGrouping = useInboxGrouping();

  const hasManualTabSelectionRef = useRef(false);

  const openInquiries = useInfiniteQuery({
    ...getOpenInquiriesV2QueryOptions(userId, selectedCancerFocusId, inboxGrouping),
    enabled: !!userId && tabSelected === 'open',
  });

  const closedInquiries = useInfiniteQuery({
    ...getClosedInquiriesV2QueryOptions(userId, selectedCancerFocusId, inboxGrouping),
    enabled: !!userId && tabSelected === 'closed',
  });

  const allInquiries = useInfiniteQuery({
    ...getAllInquiriesV2QueryOptions(userId, selectedCancerFocusId, inboxGrouping),
    enabled: !!userId && (tabSelected === 'all' || isWeb),
  });

  /** TODO: Remove filterOpenTabInquiriesForUser — redundant now that the API
   * filters server-side via conversationState: 'active', active + inactive-for-author. */
  const openInquiriesForChat = useMemo(
    () =>
      filterOpenTabInquiriesForUser(
        openInquiries.data?.pages?.flatMap((page) => page?.inquiries ?? []) ?? [],
        userId
      ),
    [openInquiries.data?.pages, userId]
  );

  const closedInquiriesForChat = useMemo(
    () =>
      filterClosedTabInquiriesForUser(
        closedInquiries.data?.pages?.flatMap((page) => page?.inquiries ?? []) ?? [],
        userId
      ),
    [closedInquiries.data?.pages, userId]
  );

  const onFocusRefetchOpenInquiries = useCallback(() => {
    openInquiries.refetch().then(
      () => undefined,
      () => undefined
    );
  }, [openInquiries.refetch]);

  useRefreshOnFocus(onFocusRefetchOpenInquiries);

  useEffect(() => {
    if (openInquiries.error) {
      console.error('Inbox openInquiriesQuery ERROR:', openInquiries.error);

      Sentry.captureException(openInquiries.error, {
        tags: { component: 'Inbox', query: 'openInquiries' },
      });
    }

    if (closedInquiries.error) {
      console.error('Inbox closedInquiriesQuery ERROR:', closedInquiries.error);

      Sentry.captureException(closedInquiries.error, {
        tags: { component: 'Inbox', query: 'closedInquiries' },
      });
    }
  }, [openInquiries.error, closedInquiries.error]);

  useEffect(() => {
    if (hasManualTabSelectionRef.current) {
      return;
    }

    if (!selectedChatId) {
      return;
    }

    if (tabSelected === 'all') {
      return;
    }

    if (!openInquiries.data) {
      return;
    }

    const selectedChatHasOpenInquiry = openInquiriesForChat.some(
      (inquiry: InquiryV2) => inquiry.conversationId === selectedChatId
    );

    if (tabSelected === 'open') {
      if (!selectedChatHasOpenInquiry) {
        const selectedInquiryQuery = queryClient.getQueryData<GetInquiryV2Response>(
          getInquiryV2QueryOptions({ conversationId: selectedChatId }).queryKey
        );

        const selectedInquiryStatus = selectedInquiryQuery?.inquiryStatus;

        const selectedInquiryFeedbackStatus = selectedInquiryQuery?.feedbackStatus;

        const shouldKeepSelectionForFeedback =
          selectedInquiryStatus === 'closed' && selectedInquiryFeedbackStatus !== 'Submitted';

        if (shouldKeepSelectionForFeedback) {
          return;
        }
      }

      return;
    }

    if (selectedChatHasOpenInquiry && tabSelected !== 'open') {
      const openRoute = TAB_ROUTES.find((r) => r.key === 'open');

      if (openRoute) {
        return onIndexChange(openRoute.index);
      }
    }

    if (!closedInquiries.data) {
      return;
    }

    const selectedChatHasClosedInquiry = closedInquiriesForChat.some(
      (inquiry: InquiryV2) => inquiry.conversationId === selectedChatId
    );

    if (selectedChatHasClosedInquiry && tabSelected !== 'closed') {
      const closedRoute = TAB_ROUTES.find((r) => r.key === 'closed');

      if (closedRoute) {
        return onIndexChange(closedRoute.index);
      }
    }
  }, [
    selectedChatId,
    closedInquiries.data,
    onIndexChange,
    queryClient,
    openInquiries.data,
    openInquiriesForChat,
    tabSelected,
    userId,
    closedInquiriesForChat,
  ]);

  useEffect(() => {
    if (!selectedChatId) {
      hasManualTabSelectionRef.current = false;
    }
  }, [selectedChatId]);

  const handleTabPress = useCallback(
    (index: number) => {
      hasManualTabSelectionRef.current = Boolean(selectedChatId);

      resetSelectedCancerFocus();

      resetSelectedChatId();

      onIndexChange(index);
    },
    [onIndexChange, resetSelectedCancerFocus, resetSelectedChatId, selectedChatId]
  );

  const { data: inquiryStats, isLoading: isStatsLoading } = useQuery({
    ...getInquiryStatsQueryOptions(userId),
    enabled: shouldFetchUserScoped(userId),
  });

  const openInquiriesCount = inquiryStats?.openCount ?? 0;

  const inquiriesClosedLastWeekCount = inquiryStats?.closedThisWeekCount ?? 0;

  const inquiriesCount =
    openInquiriesCount + (closedInquiries.data?.pages[0]?.inquiries.length ?? 0);

  const isChatSectionLoading =
    isStatsLoading ||
    (tabSelected === 'open' && openInquiries.isLoading) ||
    (tabSelected === 'closed' && closedInquiries.isLoading) ||
    (tabSelected === 'all' && allInquiries.isLoading);

  const allInquiriesForInbox = useMemo(
    () => allInquiries.data?.pages?.flatMap((page) => page?.inquiries ?? []) ?? [],
    [allInquiries.data?.pages]
  );

  const allInquiriesForChat = useMemo(
    () =>
      dedupeInquiriesByConversationId([
        ...allInquiriesForInbox,
        ...openInquiriesForChat,
        ...closedInquiriesForChat,
      ]),
    [openInquiriesForChat, closedInquiriesForChat, allInquiriesForInbox]
  );

  // Right sidebar open state. The panel itself is a shell here; feature
  // branches render their content into it. Gated off until then.
  const isSidebarEnabled = useFeatureFlag(FEATURE_FLAGS.INBOX_SIDEBAR);

  const [isSidebarOpen, setIsSidebarOpen] = useAtom(inboxSidebarOpenAtom);

  // Resizable list column (web). Width is plain React state applied as an inline
  // style; a divider drags it via DOM pointer events. The clamp lives in
  // listColumnWidth so its boundary behavior is unit-tested.
  const [listWidth, setListWidth] = useState(LIST_DEFAULT_WIDTH);

  const [isResizing, setIsResizing] = useState(false);

  const [isDividerHover, setIsDividerHover] = useState(false);

  const listWidthRef = useRef(listWidth);

  listWidthRef.current = listWidth;

  useEffect(() => {
    if (!isWeb) {
      return;
    }

    const node = document.getElementById('inbox-list-resizer');

    if (!node?.addEventListener) {
      return;
    }

    /** Teardown for an in-flight drag, run on pointerup/cancel and effect cleanup so unmounting mid-drag can't leak window listeners or leave the body unselectable. */
    let endActiveDrag: (() => void) | undefined;

    const onPointerDown = (event: PointerEvent) => {
      event.preventDefault();

      endActiveDrag?.();

      setIsResizing(true);

      const startX = event.clientX;

      const baseWidth = listWidthRef.current;

      const onPointerMove = (moveEvent: PointerEvent) => {
        setListWidth(nextListWidth(baseWidth, moveEvent.clientX - startX));
      };

      endActiveDrag = () => {
        window.removeEventListener('pointermove', onPointerMove);

        window.removeEventListener('pointerup', endActiveDrag as EventListener);

        window.removeEventListener('pointercancel', endActiveDrag as EventListener);

        document.body.style.userSelect = '';

        endActiveDrag = undefined;

        setIsResizing(false);
      };

      // Prevent text selection while dragging.
      document.body.style.userSelect = 'none';

      window.addEventListener('pointermove', onPointerMove);

      window.addEventListener('pointerup', endActiveDrag);

      window.addEventListener('pointercancel', endActiveDrag);
    };

    node.addEventListener('pointerdown', onPointerDown);

    return () => {
      node.removeEventListener('pointerdown', onPointerDown);

      endActiveDrag?.();
    };
  }, []);

  const sideBarTabKeys = ['all', 'open', 'closed', 'drafts'];

  const desktopRoutes = sideBarTabKeys
    .map((key) => tabState.routes.find((route) => route.key === key))
    .filter((route): route is (typeof tabState.routes)[number] => Boolean(route));

  // Divider seam: subtle hairline at rest, gray on hover, soft green while
  // dragging. Accent is reserved for the active drag only.
  let dividerLineClass = 'bg-outline-100';

  if (isResizing) {
    dividerLineClass = 'bg-primary-400';
  } else if (isDividerHover) {
    dividerLineClass = 'bg-outline-300';
  }

  return (
    // TODO(inbox-layout): the list column is resizable via the divider below.
    // Follow-up: make the chat | sidebar boundary resizable too, and persist the
    // chosen widths across sessions.
    <Box className="flex min-h-0 w-full flex-1 flex-col md:min-h-0 md:flex-row md:items-stretch">
      <VStack
        testID="inbox-list-column"
        className="relative w-full shrink-0 pb-4 pt-0 md:w-auto md:flex-none md:shrink-0"
        // DEV ENG-610: three panes (inquiry list | chat | right sidebar). The
        // list width is draggable via the divider below; default 512px.
        style={isWeb ? { width: listWidth } : undefined}>
        <InboxListHeader
          routes={desktopRoutes}
          tabSelected={tabSelected}
          onTabPress={handleTabPress}
          inquiryCancerTypesData={inquiryCancerTypesQuery.data}
          selectedCancerFocus={selectedCancerFocus}
          setSelectedCancerFocus={setSelectedCancerFocus}
          resetSelectedChatId={resetSelectedChatId}
        />

        {tabSelected === 'drafts' && !selectedCancerFocus ? (
          <MobileDraftsTab />
        ) : (
          <DesktopInquiriesListView
            inquiryCancerTypesData={inquiryCancerTypesQuery.data}
            selectedChatId={selectedChatId}
            openInquiries={openInquiries}
            closedInquiries={closedInquiries}
            allInquiries={allInquiries}
          />
        )}

        <NewSupportMessageFab />
      </VStack>

      {/* Drag handle to resize the list column. Web only; uses DOM pointer
          events wired in the effect above. The shadow on hover signals it as
          draggable. Mouse-only convenience: there is no keyboard resize, so it
          stays out of the tab order and hidden from assistive tech rather than
          exposing a focusable control that does nothing on keyboard. The list
          is fully usable at the default width. A keyboard-operable
          role="separator" splitter is a possible follow-up. */}
      {isWeb ? (
        <Pressable
          nativeID="inbox-list-resizer"
          testID="inbox-list-resizer"
          focusable={false}
          aria-hidden
          onHoverIn={() => setIsDividerHover(true)}
          onHoverOut={() => setIsDividerHover(false)}
          // Zero net width (-mx-1 cancels w-2) so the two panes sit flush
          // against the centered seam; the 8px box is an invisible grab zone
          // that overlaps both panes. z-10 keeps it above the panes for the line
          // and pointer capture.
          className="relative z-10 -mx-1 hidden w-2 shrink-0 items-center justify-center self-stretch web:cursor-col-resize data-[focus-visible=true]:ring-0 md:flex">
          <Box
            className={twJoin(
              'h-full w-px web:transition-colors web:duration-150',
              dividerLineClass
            )}
          />
        </Pressable>
      ) : null}

      {isWeb ? (
        <VStack
          testID="inbox-chat-column"
          className="min-h-[min(70vh,560px)] w-full min-w-0 flex-1 border-t border-outline-100 md:min-h-0 md:w-auto md:min-w-0 md:flex-1 md:border-t-0">
          <ChatSection
            id={selectedChatId}
            openInquiriesCount={openInquiriesCount}
            inquiriesClosedLastWeekCount={inquiriesClosedLastWeekCount}
            inquiriesCount={inquiriesCount}
            allInquiries={allInquiriesForChat}
            tabSelected={tabSelected}
            isLoading={isChatSectionLoading}
          />
        </VStack>
      ) : null}

      {/* Third pane: inbox sidebar (shell). Shows on web when the flag is on and
          the panel is open; feature branches render content into it. */}
      {isWeb && isSidebarEnabled ? (
        <InboxSidebarPanel
          isOpen={isSidebarOpen}
          isCompactLayout={false}
          onClose={() => setIsSidebarOpen(false)}
        />
      ) : null}
    </Box>
  );
});
